import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faHourglass } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import styled, { type CSSProperties } from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { BottomPortal } from '@sorare/core/src/atoms/layout/BottomPortal';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { ScarcityBackground } from '@sorare/core/src/components/card/ScarcityBackground';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useHasLegacyEssence } from '@sorare/core/src/hooks/commonGame/useHasLegacyEssence.ts';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import {
  getSpecialSetCardPackGroupsThatAreNotFeatured,
  isSpecialCardSet,
} from '@sorare/core/src/lib/cardSets';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { COMMON_GAME_SET } from '@sorare/core/src/set';
import {
  desktopAndAbove,
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { BuyGemsBlock } from '#src/components/commonCard/BuyGemsBlock/index.tsx';
import { BuyGemsButtonBlock } from '#src/components/commonCard/BuyGemsButtonBlock/index.tsx';
import { LegacyPacksTile } from '#src/components/commonCard/BuyPacks/LegacyPacksTile/index.tsx';
import { SeriesCardPackGroup } from '#src/components/commonCard/BuyPacks/SeriesCardPackGroup/index.tsx';
import { SeriesDailyClaimBlock } from '#src/components/commonCard/BuyPacks/SeriesDailyClaimBlock/index.tsx';
import { SeriesDailyClaimModule } from '#src/components/commonCard/BuyPacks/SeriesDailyClaimModule/index.tsx';
import { usePackAssets } from '#src/components/commonCard/PacksTypePage/usePackAssets.ts';

import {
  FeaturedSeriesPackGroup,
  canDisplayFeaturedSeriesPackGroup,
} from '../BuyPacks/FeaturedSeriesPackGroup/index.tsx';
import type {
  CommonPacksPage_marketRoot,
  CommonPacksPage_tokenRoot,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  container-type: inline-size;
`;

const FullWidthWrapper = styled(Vertical)`
  padding: calc(var(--unit) * 7) 0;
  &.isSimplifiedNav {
    padding-top: calc(var(--unit) * 2);
  }

  @container ${tabletAndAbove} {
    padding: calc(var(--unit) * 8) 0;
    &.isSimplifiedNav {
      padding: calc(var(--unit) * 8) 0;
    }
    gap: var(--triple-unit);
  }
`;

const Positioned = styled.div`
  position: sticky;
  left: 0;
  right: 0;
  top: var(--current-stack-height);
  height: 0;
  width: 100%;
  z-index: -1;
`;

const BackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  isolation: isolate;
  height: calc(var(--100vh) - var(--current-stack-height));
  background: #0f1c28;
`;

const Wrapper = styled(Vertical)`
  gap: var(--double-unit);
  @container ${laptopAndAbove} {
    display: grid;
    grid-template-columns: repeat(max(calc(var(--nb-items) / 2), 2), 1fr);
    gap: var(--triple-unit);
  }
  @container ${desktopAndAbove} {
    grid-template-columns: repeat(max(var(--nb-items), 2), 1fr);
  }
`;

const SeriesDailyClaimWrapper = styled(Horizontal)`
  justify-content: flex-end;
  width: calc(100 * var(--vw) - var(--quadruple-unit));
  @container ${tabletAndAbove} {
    width: 100%;
    padding: 0 var(--double-unit);
    position: sticky;
    bottom: calc(var(--unit) * 6);
    z-index: 1;
    height: 0;
  }
  @container ${laptopAndAbove} {
    display: none;
  }
`;

const Header = styled(Horizontal).attrs({ center: true })`
  gap: var(--double-unit);
  padding: var(--double-unit);
  background: url(${COMMON_GAME_SET.visual.commonPacks.headerImage}) center
    center / cover no-repeat;
  border-radius: var(--double-unit);
  width: calc(100% - var(--quadruple-unit));
  box-shadow:
    0 0 8px 0 rgba(7, 243, 208, 0.5),
    0 0 4px 0 rgba(7, 243, 208, 0.25) inset;
  border: 1px solid var(--c-set-main-color);
  margin: 0 auto;
  flex-direction: row-reverse;
  @container ${laptopAndAbove} {
    margin: 0;
    width: 100%;
    border: none;
    box-shadow: none;
    background: none;
    display: flex;
    flex-direction: column;
    padding: 0 var(--triple-unit);
  }
`;

const SeriesLogo = styled.img`
  height: 24px;
  @container ${laptopAndAbove} {
    height: 48px;
    max-width: 280px;
    object-fit: contain;
  }
`;

const DesktopLayout = styled(Vertical)`
  @container ${laptopAndAbove} {
    min-height: max(55vh, 540px);
    display: grid;
    grid-template-columns: 1fr 250px;
    gap: var(--triple-unit);
  }
`;

const DesktopRightColumn = styled(Vertical).attrs({ gap: 3 })`
  display: none;
  @container ${laptopAndAbove} {
    display: flex;
    gap: var(--triple-unit);
  }
`;
const HideOnDesktop = styled.div`
  @container ${laptopAndAbove} {
    display: none;
  }
`;
const FeaturedPack = styled(Horizontal)`
  @container ${laptopAndAbove} {
    grid-column: 1/-1;
  }
`;
type Props = {
  refetch: () => Promise<unknown>;
  marketRoot: CommonPacksPage_marketRoot;
  tokenRoot?: CommonPacksPage_tokenRoot;
};
export const CommonPacksPage = ({ refetch, marketRoot, tokenRoot }: Props) => {
  const {
    flags: { enableNavToggleProSet = false },
  } = useFeatureFlags();
  const isSimplifiedNav = useSeriesNav();
  const isTabletOrAbove = useIsTabletAndAbove();
  const isLaptopOrAbove = useIsLaptop();
  const hasLegacyEssence = useHasLegacyEssence();
  const gemsPacks =
    tokenRoot?.purchasableItems.filter(
      isTypeFn('InGameCurrencyPurchasableItem')
    ) || [];

  const featuredCardPackGroup = marketRoot.cardPackGroups.find(
    ({ featured, cardSet }) => isSpecialCardSet(cardSet?.slug) && featured
  );
  const specialSetCardPackGroups =
    getSpecialSetCardPackGroupsThatAreNotFeatured(marketRoot.cardPackGroups);
  const specialSetCardPackGroupsLength = specialSetCardPackGroups?.length || 0;

  const { bgImage } = usePackAssets({
    cardPackGroupId: marketRoot.cardPackGroups?.[0]?.id || '',
  });

  const dailyClaimTask = marketRoot.tasks.filter(isTypeFn('Task')).at(0);

  return (
    <Root>
      <PullToRefetch refetch={refetch}>
        <Positioned>
          <BackgroundWrapper>
            {!!isLaptopOrAbove && (
              <ScarcityBackground
                rarity={Rarity.common}
                editionName={COMMON_GAME_SET.cardSet.activeSlug}
              />
            )}
          </BackgroundWrapper>
        </Positioned>
        <FullWidthWrapper className={classNames({ isSimplifiedNav })}>
          {!!(!isSimplifiedNav || isLaptopOrAbove) && (
            <Header>
              <Chip color="set">
                <FontAwesomeIcon icon={faHourglass} />
                <TimeLeft
                  time={COMMON_GAME_SET.endDate}
                  options={{ totalDays: true }}
                />
              </Chip>
              <SeriesLogo
                src={COMMON_GAME_SET.visual.commonPacks.logoImage}
                alt=""
              />
            </Header>
          )}
          <Container maxWidth={1320}>
            <DesktopLayout>
              <Wrapper
                gap={3}
                className={classNames({
                  'featured-pack': !!featuredCardPackGroup,
                })}
                style={
                  {
                    '--nb-items': specialSetCardPackGroupsLength,
                  } as CSSProperties
                }
              >
                {canDisplayFeaturedSeriesPackGroup(featuredCardPackGroup) && (
                  <FeaturedPack>
                    <FeaturedSeriesPackGroup
                      cardPackGroup={featuredCardPackGroup}
                    />
                  </FeaturedPack>
                )}
                {specialSetCardPackGroups?.map(group => (
                  <SeriesCardPackGroup
                    key={group.id}
                    cardPackGroup={group}
                    wide={specialSetCardPackGroupsLength < 4}
                  />
                ))}
                <HideOnDesktop>
                  <BuyGemsBlock gemsPacks={gemsPacks} />
                </HideOnDesktop>
                {!!(!isSimplifiedNav || enableNavToggleProSet) &&
                  !!hasLegacyEssence && (
                    <HideOnDesktop>
                      <LegacyPacksTile bgImage={bgImage} />
                    </HideOnDesktop>
                  )}
              </Wrapper>
              <DesktopRightColumn>
                {!!dailyClaimTask && (
                  <SeriesDailyClaimBlock
                    task={dailyClaimTask}
                    refetch={refetch}
                  />
                )}
                <BuyGemsButtonBlock />
                {!!(!isSimplifiedNav || enableNavToggleProSet) &&
                  !!hasLegacyEssence && <LegacyPacksTile bgImage={bgImage} />}
              </DesktopRightColumn>
            </DesktopLayout>
          </Container>
        </FullWidthWrapper>
      </PullToRefetch>
      <ConditionalWrapper
        wrap={!isLaptopOrAbove}
        Wrapper={isTabletOrAbove ? BottomPortal : BottomPortal.Above}
      >
        <SeriesDailyClaimWrapper>
          {!!dailyClaimTask && (
            <SeriesDailyClaimModule task={dailyClaimTask} refetch={refetch} />
          )}
        </SeriesDailyClaimWrapper>
      </ConditionalWrapper>
    </Root>
  );
};

CommonPacksPage.fragments = {
  marketRoot: gql`
    fragment CommonPacksPage_marketRoot on MarketRoot {
      id
      tasks(sport: $sport, genre: COMMON_GAME_DAILY_CLAIM) {
        id
        name
        ...SeriesDailyClaimModule_task
        ...SeriesDailyClaimBlock_task
      }
      cardPackGroups(sport: $sport) {
        id
        cardPacks {
          slug
        }
        featured
        ...getSpecialSetCardPackGroupsThatAreNotFeatured_cardPackGroup
        ...FeaturedSeriesPackGroup_cardPackGroup
        ...SeriesCardPackGroup_cardPackGroup
      }
    }
    ${getSpecialSetCardPackGroupsThatAreNotFeatured.fragments.cardPackGroup}
    ${FeaturedSeriesPackGroup.fragments.cardPackGroup}
    ${SeriesCardPackGroup.fragments.cardPackGroup}
    ${SeriesDailyClaimModule.fragments.task}
    ${SeriesDailyClaimBlock.fragments.task}
  ` as TypedDocumentNode<CommonPacksPage_marketRoot>,
  tokenRoot: gql`
    fragment CommonPacksPage_tokenRoot on TokenRoot {
      purchasableItems(sport: FOOTBALL) {
        slug
        ... on InGameCurrencyPurchasableItem {
          slug
          ...BuyGemsBlock_inGameCurrencyPurchasableItem
        }
      }
    }
    ${BuyGemsBlock.fragments.inGameCurrencyPurchasableItem}
  ` as TypedDocumentNode<CommonPacksPage_tokenRoot>,
};
